import { findLastIndex } from 'lodash';
import dayjs from '~/timeInit';

import type { ProjectTask, ProjectTaskStatus } from '~/services/ContractingService';

// Must match svc-contracting's moveTask so the optimistic order matches the saved one.
export function moveTask(
  tasks: Array<ProjectTask>,
  taskId: string,
  status: ProjectTaskStatus,
  beforeTaskId?: string,
): Array<ProjectTask> {
  const task = tasks.find((item) => item.id === taskId);
  if (!task || taskId === beforeTaskId) {
    return tasks;
  }

  const moved = { ...task, status };
  const remaining = tasks.filter((item) => item.id !== taskId);
  const beforeIndex = remaining.findIndex((item) => item.id === beforeTaskId);
  const lastInColumnIndex = findLastIndex(remaining, (item) => item.status === status);
  const insertAt = beforeIndex !== -1 ? beforeIndex : lastInColumnIndex + 1 || remaining.length;

  return [...remaining.slice(0, insertAt), moved, ...remaining.slice(insertAt)];
}

export type TimelineHealth = 'red' | 'amber' | 'green';

function describeDue(daysLeft: number) {
  if (daysLeft < 0) {
    return `Overdue by ${pluralize(-daysLeft, 'day')}`;
  }
  return daysLeft === 0 ? 'Due today' : `Due in ${pluralize(daysLeft, 'day')}`;
}

function pluralize(count: number, unit: string) {
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

export interface HealthResult {
  health: TimelineHealth;
  reason: string;
}

export function timelineHealth(task: ProjectTask, today = dayjs()): HealthResult | null {
  if (task.status === 'CANCELLED') {
    return null;
  }
  if (task.status === 'COMPLETED') {
    return { health: 'green', reason: 'Completed.' };
  }
  const startOfToday = today.startOf('day');
  const daysLeft = dayjs(task.endDate).diff(startOfToday, 'day');
  if (daysLeft < 0) {
    return { health: 'red', reason: `${describeDue(daysLeft)}.` };
  }
  const daysSinceStart = startOfToday.diff(dayjs(task.startDate), 'day');
  if (task.status === 'NOT_STARTED' && daysSinceStart > 0) {
    return { health: 'amber', reason: `Not started. It was due to start ${pluralize(daysSinceStart, 'day')} ago.` };
  }
  return { health: 'green', reason: `On track. ${describeDue(daysLeft)}.` };
}
